import { Component } from '@theme/component';
 
/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
 
  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;
 
  connectedCallback() {
    super.connectedCallback();
    this.play();
  }
 
  next() {
    this.current += 1;
  }
 
  previous() {
    this.current -= 1;
  }
 
  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
 
    this.paused = false;
 
    this.#interval = setInterval(() => {
      this.next();
    }, interval);
  }
 
  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }
 
  get paused() {
    return this.hasAttribute('paused');
  }
 
  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }
 
  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
    this.updatePlayPauseState(false);
  }
 
  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;
 
    this.pause();
    this.play();
    this.updatePlayPauseState(true);
  }
 
  get autoplay() {
    return Boolean(this.autoplayInterval);
  }
 
  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
 
    if (Number.isNaN(value)) return undefined;
 
    return value * 1000;
  }
 
  get current() {
    return this.#current;
  }
 
  set current(current) {
    this.#current = current;
 
    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }
 
    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
 
    this.updateAnnouncementBar(relativeIndex);
  }
 
  /**
   * [CUSTOM] Updates announcement bar based on the active slide colors
   * @param {number} [currentSlideIndex] - Current slide index
   */
  updateAnnouncementBar(currentSlideIndex) {
    if (currentSlideIndex == null) return;
 
    /** @type {HTMLElement | null} */
    const announcementBar = document.querySelector('.announcement-bar');
    const slides = announcementBar?.querySelectorAll('slideshow-slide');
    if (slides && slides.length > 1) {
      const currentSlide = slides[currentSlideIndex];
      const backgroundColor = currentSlide?.getAttribute('data-bg-color');
      const textColor = currentSlide?.getAttribute('data-text-color');
      const iconColor = currentSlide?.getAttribute('data-icon-color') || textColor;
 
      if (announcementBar && backgroundColor && textColor) {
        announcementBar.style.backgroundColor = backgroundColor;
        announcementBar.style.color = textColor;
        announcementBar.style.setProperty('--announcement-icon-color', iconColor);
      }
    }
  }
 
  /**
   * [CUSTOM] Updates play/pause initiated state
   * @param {Boolean} [slideEnabled] - true for play, false for pause
   */
  updatePlayPauseState(slideEnabled) {
    if (slideEnabled == null) return;
 
    /** @type {HTMLElement | null} */
    const pauseButton = this.querySelector('[data-slideshow-pause]');
    /** @type {HTMLElement | null} */
    const playButton = this.querySelector('[data-slideshow-play]');
    if (pauseButton && playButton) {
      pauseButton.style.display = slideEnabled ? 'block' : 'none';
      playButton.style.display = slideEnabled ? 'none' : 'block';
    }
  }
}
 
if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}